import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import RichMessage from '../RichMessage';
import { ChatBlocks } from '../ChatBlocks';
import { ThinkingPanel } from '../ThinkingPanel';
import {
  Bot, X, Send, Loader2, Minimize2, Maximize2, Sparkles, MessageSquare, Mic, MicOff, Square, AlertTriangle, ExternalLink
} from 'lucide-react';
import { useVoiceInput } from '../../hooks/useVoiceInput';

const API = '/api';

const FloatingChat = ({ onClose, isMinimized, onToggleMinimize }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [liveRunId, setLiveRunId] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const { isRecording, isTranscribing, duration, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    onTranscript: (text) => {
      setInput(prev => prev ? prev + ' ' + text : text);
      inputRef.current?.focus();
    }
  });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when opened
  useEffect(() => {
    if (!isMinimized) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isMinimized]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput('');
    setLoading(true);
    
    // Add user message immediately
    const tempUserMsg = {
      id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage
    };
    setMessages(prev => [...prev, tempUserMsg]);

    // Phase 14.1 — live ThinkingPanel polling key
    const liveRunId = `run_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    setLiveRunId(liveRunId);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          surface: 'floating_chat',
          live_run_id: liveRunId,
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);

        // SaaS Phase 1 — handle budget-exceeded clean error envelope
        if (data.error_code === 'AI_BUDGET_EXCEEDED') {
          toast.error('Monthly AI limit reached', {
            description: data.error || 'Upgrade your plan to keep using AI.',
            action: { label: 'Upgrade', onClick: () => { window.location.href = '/billing'; } },
          });
          setMessages(prev => [...prev, {
            id: `budget_${Date.now()}`,
            role: 'assistant',
            content: `🚧 **Monthly AI limit reached** — ${data.error || ''}\n\nYou've used ${data.used ?? '?'} / ${data.limit ?? '?'} ${data.metric_key} this month. [Upgrade your plan](/billing) to keep using AI.`,
          }]);
          return;
        }

        // Add assistant response (carry tool_failures + pending_actions + scratchpad for ChatBlocks rendering)
        const assistantMsg = {
          id: `resp_${Date.now()}`,
          role: 'assistant',
          content: data.response,
          tool_failures: data.tool_failures || [],
          pending_actions: data.pending_actions || [],
          scratchpad: data.scratchpad || [],
        };
        setMessages(prev => [...prev, assistantMsg]);
        
        // Check for pending actions
        if (data.pending_actions?.length > 0) {
          toast.info('Action requires confirmation', {
            description: 'Check the AI page to confirm the action'
          });
        }

        // Surface AI tool failures (Phase 0.5: error glossary toast + Deck link)
        if (data.tool_failures?.length > 0) {
          const f = data.tool_failures[0];
          toast.error(`${f.error_code || 'AI-TOOL'} · ${f.capability_name || f.capability_id}`, {
            description: 'AI hit an error. The full trail is in your Deck.',
            action: { label: 'Open Deck', onClick: () => { window.location.href = '/ai'; } },
          });
        }
      } else {
        const error = await res.json();
        toast.error('AI Error', { description: error.detail });
      }
    } catch (err) {
      console.error('Chat error:', err);
      toast.error('Failed to send message');
    } finally {
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isMinimized) {
    return (
      <button
        onClick={onToggleMinimize}
        className="fixed right-6 bottom-6 w-14 h-14 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-lg hover:shadow-xl orka-transition-colors z-50 flex items-center justify-center group"
        data-testid="chat-fab"
      >
        <Bot className="w-6 h-6 group-hover:scale-110 transition-transform" />
        {messages.length > 0 && (
          <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs">
            {messages.filter(m => m.role === 'assistant').length}
          </Badge>
        )}
      </button>
    );
  }

  return (
    <div 
      className="fixed right-6 bottom-6 w-96 h-[500px] bg-background rounded-2xl shadow-2xl border flex flex-col z-50 overflow-hidden"
      data-testid="floating-chat"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gradient-to-r from-violet-600 to-indigo-600 text-white">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <Bot className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Orka AI</h3>
            <p className="text-xs text-white/70">Your AI Assistant</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20" 
            onClick={onToggleMinimize}
          >
            <Minimize2 className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-white hover:bg-white/20" 
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h3 className="font-semibold mb-2">How can I help?</h3>
            <p className="text-xs text-muted-foreground mb-4 max-w-[200px]">
              Ask me about your tasks, schedule, or anything else!
            </p>
            <div className="space-y-2 w-full">
              {[
                "What's on my agenda today?",
                "Show my backlog tasks",
                "Create a task for tomorrow"
              ].map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => setInput(suggestion)}
                  className="w-full text-left text-xs px-3 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                >
                  <MessageSquare className="w-3 h-3 inline mr-2 text-violet-500" />
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={cn(
                  "flex",
                  msg.role === 'user' ? "justify-end" : "justify-start"
                )}
              >
                <div className={cn(
                  "max-w-[85%] rounded-2xl px-3 py-2",
                  msg.role === 'user' 
                    ? "bg-gradient-to-br from-violet-600 to-indigo-600 text-white" 
                    : "bg-muted"
                )}>
                  {msg.role === 'user' ? (
                    <p className="text-xs">{msg.content}</p>
                  ) : (
                    <>
                      {msg.scratchpad && msg.scratchpad.length > 0 && (
                        <div className="mb-1.5">
                          <ThinkingPanel scratchpad={msg.scratchpad} />
                        </div>
                      )}
                      <RichMessage content={msg.content} className="text-xs" />
                      <ChatBlocks
                        message={msg}
                        onConfirmed={(paId, result) => {
                          // Remove the confirmed pending from the message and append a follow-up step.
                          setMessages(prev => prev.map(m => {
                            if (m.id !== msg.id) return m;
                            const remaining = (m.pending_actions || []).filter(p => p.pending_action_id !== paId);
                            const newStep = result?.success ? {
                              step: (m.scratchpad?.length || 0) + 1,
                              capability_id: result.capability_id,
                              capability_name: result.capability_id,
                              status: 'completed',
                              action_run_id: result.action_run_id,
                              undo_deadline: result.undo_deadline,
                              summary: 'confirmed & executed',
                            } : null;
                            return {
                              ...m,
                              pending_actions: remaining,
                              scratchpad: newStep ? [...(m.scratchpad || []), newStep] : (m.scratchpad || []),
                            };
                          }));
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-2xl px-2 py-2 max-w-[95%] w-full">
                  <ThinkingPanel isLive defaultOpen liveRunId={liveRunId} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input */}
      <div className="p-3 border-t">
        <div className="flex gap-1.5 items-center">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={isRecording ? "Listening..." : isTranscribing ? "Transcribing..." : "Ask Orka AI..."}
            className="flex-1 rounded-full text-sm h-9"
            disabled={isRecording || isTranscribing}
            data-testid="chat-input"
          />
          {isRecording ? (
            <>
              <span className="text-[10px] text-red-500 font-mono animate-pulse">{duration}s</span>
              <Button onClick={stopRecording} size="icon" className="rounded-full h-8 w-8 bg-red-500 hover:bg-red-600 animate-pulse" data-testid="stop-recording-btn">
                <Square className="w-3 h-3 fill-current" />
              </Button>
            </>
          ) : (
            <Button onClick={startRecording} size="icon" variant="ghost" disabled={loading || isTranscribing} className="rounded-full h-8 w-8 hover:text-violet-600" data-testid="voice-input-btn">
              {isTranscribing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
            </Button>
          )}
          <Button 
            onClick={sendMessage}
            disabled={!input.trim() || loading}
            size="icon"
            className="rounded-full h-9 w-9 bg-gradient-to-br from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700"
            data-testid="send-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FloatingChat;
